import * as React from 'react';
import { Route, Routes } from 'react-router';
import type { Registry, Contribution } from './registry.ts';
import { slotNodes } from './slot.ts';
import type {
  ExtensionRegistration,
  ExtensionSlotProps,
  NotificationComponentProps,
  NotificationOptions,
  PiletApi,
  PiletMetadata,
} from '../index.ts';

// The React half of the plugin host: the `app` object each plugin gets, the
// slot component (`app.Extension`), the page routes and the notifications,
// all reading the registry. Behaviour follows Piral's: a contribution is
// rendered with `params` = its registration defaults merged with the slot's
// params, inside an error boundary of its own, in registration order unless
// the slot passes `order`; a slot's `wrap` draws around each contribution,
// outside its boundary (slot.ts).

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'plugin-component': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & { origin: string };
    }
  }
}

const fragment = (children: React.ReactNode, key?: string) => <React.Fragment key={key}>{children}</React.Fragment>;

/** Catches a contribution's or a page's render error so the rest of the screen stands. */
class Boundary extends React.Component<
  { pilet: string; kind: 'extension' | 'page'; app: PiletApi; children: React.ReactNode },
  { error?: unknown }
> {
  state: { error?: unknown } = {};

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    console.error(`[${this.props.pilet}] Exception in component of type "${this.props.kind}".`, error);
    const { app, kind, pilet } = this.props;
    setTimeout(() => app.emit('unhandled-error', { errorType: kind, error, pilet }));
  }

  render() {
    if (this.state.error) return <span className={`host-error host-error-${this.props.kind}`}>Error: {this.props.kind}</span>;
    return <React.Suspense fallback={null}>{this.props.children}</React.Suspense>;
  }
}

/**
 * Wraps what a plugin registered: the boundary, the `piral` prop Piral
 * passed, and a `<plugin-component origin="…">` element around it, as
 * Piral's `<piral-component>`. The element is `display: contents` (style.css),
 * so it draws no box; the theme's selectors such as
 * `.region-kind-stack > plugin-component > .panel-grow` rely on it.
 */
function wrap<P>(Component: React.ComponentType<P>, app: PiletApi, kind: 'extension' | 'page'): React.ComponentType<P> {
  const Wrapped = (props: P) => (
    <plugin-component origin={app.meta.name}>
      <Boundary pilet={app.meta.name} kind={kind} app={app}>
        <Component {...props} piral={app} />
      </Boundary>
    </plugin-component>
  );
  Wrapped.displayName = `Plugin(${Component.displayName || Component.name || kind})`;
  return Wrapped;
}

/** The registry's value, re-read on every change and kept when unchanged. */
function useRegistry<T>(registry: Registry, read: () => T): T {
  return React.useSyncExternalStore((notify) => registry.subscribe(notify), read, read);
}

/** The slot component: one per host, shared by every plugin as `app.Extension`. */
export function createExtensionSlot(registry: Registry) {
  function ExtensionSlot<T = string>(props: ExtensionSlotProps<T>): React.ReactElement | null {
    const { name, render = fragment, empty, params, children, emptySkipsRender = false, order, wrap } = props;
    const contributions = useRegistry(registry, () => registry.extensions(name as string));
    const isEmpty = contributions.length === 0 && typeof empty === 'function';
    const ordered = (order ? order([...contributions] as ExtensionRegistration[]) : contributions) as readonly Contribution[];
    const content = isEmpty ? [fragment(empty!(params as any), 'empty')] : slotNodes(ordered, { params, children, wrap });
    if (isEmpty && emptySkipsRender) return content[0] as React.ReactElement;
    return render(content);
  }
  ExtensionSlot.displayName = 'ExtensionSlot';
  return ExtensionSlot;
}

/** Builds a plugin's `app` object over the shared registry. */
export function createPiletApi(registry: Registry, meta: PiletMetadata, Extension: PiletApi['Extension']): PiletApi {
  const app: PiletApi = {
    meta,
    on(type, callback) {
      registry.on(meta.name, type as string, callback);
      return app;
    },
    once(type, callback) {
      const once = (arg: any) => {
        registry.off(type as string, once);
        callback(arg);
      };
      registry.on(meta.name, type as string, once);
      return app;
    },
    off(type, callback) {
      registry.off(type as string, callback);
      return app;
    },
    emit(type, arg) {
      registry.emit(type as string, arg);
      return app;
    },
    registerApi(name, implementation) {
      registry.registerApi(meta.name, name, implementation);
    },
    getApi: ((name: string) => registry.getApi(name)) as PiletApi['getApi'],
    tryGetApi: ((name: string) => registry.tryGetApi(name)) as PiletApi['tryGetApi'],
    registerExtension(name, Component, defaults) {
      return registry.registerExtension(name as string, {
        component: wrap(Component as React.ComponentType<any>, app, 'extension'),
        reference: Component,
        defaults,
        pilet: meta.name,
      });
    },
    unregisterExtension(name, Component) {
      registry.unregisterExtension(name as string, Component);
    },
    registerPage(route, Component, pageMeta = {}) {
      return registry.registerPage({ route, component: wrap(Component as React.ComponentType<any>, app, 'page'), meta: pageMeta, pilet: meta.name });
    },
    unregisterPage(route) {
      registry.unregisterPage(route);
    },
    Extension,
    showNotification(content, options = {}) {
      return registry.showNotification(meta.name, content, options);
    },
  };
  return app;
}

/** The pages, each on its own route, and `NotFound` for any other address. */
export const PageRoutes: React.FC<{ registry: Registry; NotFound: React.ComponentType }> = ({ registry, NotFound }) => {
  const pages = useRegistry(registry, () => registry.pages());
  return (
    <Routes>
      {pages.map(({ route, component }) => {
        const Page = component as React.ComponentType;
        return <Route key={route} path={route} element={<Page />} />;
      })}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export interface NotificationViews {
  Host: React.FC<{ children: React.ReactNode }>;
  Toast: React.FC<{ options: NotificationOptions; onClose(): void; children: React.ReactNode }>;
}

/** The notifications on screen, drawn with the shell's host and toast components. */
export const Notifications: React.FC<{ registry: Registry; views: NotificationViews }> = ({ registry, views: { Host, Toast } }) => {
  const list = useRegistry(registry, () => registry.notifications());
  // The host is drawn even when empty, as Piral's was.
  return (
    <Host>
      {list.map(({ id, content, options }) => {
        const onClose = () => registry.closeNotification(id);
        let body: React.ReactNode;
        if (typeof content === 'string' || React.isValidElement(content)) body = content as React.ReactNode;
        else {
          const Content = content as React.ComponentType<NotificationComponentProps>;
          body = <Content onClose={onClose} options={options} />;
        }
        return (
          <Toast key={id} options={options} onClose={onClose}>
            {body}
          </Toast>
        );
      })}
    </Host>
  );
};
